import axios, { AxiosError } from 'axios'

/**
 * 将snake_case转换为camelCase
 */
function toCamelCase(str: string): string {
  return str.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase())
}

/**
 * 递归转换对象键名从snake_case到camelCase
 */
function convertKeysToCamelCase(obj: any): any {
  if (obj === null || obj === undefined) {
    return obj
  }
  
  if (Array.isArray(obj)) {
    return obj.map(convertKeysToCamelCase)
  }
  
  if (typeof obj === 'object') {
    const newObj: any = {}
    for (const key in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, key)) {
        const camelKey = toCamelCase(key)
        newObj[camelKey] = convertKeysToCamelCase(obj[key])
      }
    }
    return newObj
  }
  
  return obj
}

/**
 * API 错误类型
 */
export interface ApiError {
  status: number
  code?: string
  message: string
  details?: string
  field?: string
}

/**
 * 解析API错误
 */
export function parseApiError(error: unknown): ApiError {
  if (axios.isAxiosError(error)) {
    const axiosError = error as AxiosError<any>
    const response = axiosError.response
    
    if (response) {
      const data = response.data
      
      // FastAPI ValidationError 格式
      if (data?.detail && Array.isArray(data.detail)) {
        const validationErrors = data.detail.map((e: any) => 
          `${e.loc?.join('.')} - ${e.msg}`
        ).join('; ')
        
        return {
          status: response.status,
          code: 'VALIDATION_ERROR',
          message: '数据验证失败',
          details: validationErrors
        }
      }
      
      // FastAPI HTTPException 格式
      if (data?.detail && typeof data.detail === 'string') {
        return {
          status: response.status,
          code: 'API_ERROR',
          message: data.detail
        }
      }
      
      // 其他格式
      if (data?.message) {
        return {
          status: response.status,
          code: data.code || 'API_ERROR',
          message: data.message,
          details: data.details
        }
      }
      
      // 通用HTTP错误
      const httpErrorMessages: Record<number, string> = {
        400: '请求参数错误',
        401: '未授权，请重新登录',
        403: '没有权限执行此操作',
        404: '请求的资源不存在',
        409: '数据冲突',
        422: '数据验证失败',
        429: '请求过于频繁，请稍后再试',
        500: '服务器内部错误，请稍后再试',
        502: '服务网关错误',
        503: '服务暂时不可用',
        504: '网关超时'
      }
      
      return {
        status: response.status,
        code: 'HTTP_ERROR',
        message: httpErrorMessages[response.status] || `请求失败 (${response.status})`
      }
    }
    
    // 网络错误
    if (axiosError.code === 'ECONNABORTED') {
      return {
        status: 0,
        code: 'TIMEOUT',
        message: '请求超时，请检查网络连接'
      }
    }
    
    if (axiosError.code === 'ERR_NETWORK') {
      return {
        status: 0,
        code: 'NETWORK_ERROR',
        message: '网络连接失败，请检查网络'
      }
    }
    
    return {
      status: 0,
      code: 'UNKNOWN',
      message: axiosError.message || '未知错误'
    }
  }
  
  // 非Axios错误
  if (error instanceof Error) {
    return {
      status: 0,
      code: 'CLIENT_ERROR',
      message: error.message
    }
  }
  
  return {
    status: 0,
    code: 'UNKNOWN',
    message: '发生未知错误'
  }
}

/**
 * 格式化错误消息用于显示
 */
export function formatErrorMessage(error: ApiError): { title: string; message?: string } {
  return {
    title: error.message,
    message: error.details
  }
}

/**
 * API 客户端配置
 */
const api = axios.create({
  baseURL: '/api/v1',
  timeout: 120000, // 增加到120秒，给LLM证据抽取足够时间
  headers: {
    'Content-Type': 'application/json',
  },
})

// 请求拦截器 - 添加认证Token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// 响应拦截器 - 转换snake_case为camelCase并处理错误
api.interceptors.response.use(
  (response) => {
    // 转换响应数据的键名为camelCase
    if (response.data) {
      response.data = convertKeysToCamelCase(response.data)
    }
    return response
  },
  (error) => {
    // 解析错误信息
    const apiError = parseApiError(error)
    
    // 记录错误日志
    console.error('[API Error]', apiError)
    
    if (error.response?.status === 401) {
      // Token过期，清除并跳转登录
      localStorage.removeItem('token')
      window.location.href = '/login'
    }
    
    // 将解析后的错误附加到原始错误
    error.apiError = apiError
    
    return Promise.reject(error)
  }
)

export default api

